import React, { useState } from 'react';
import { Worker, Viewer as PDFViewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';
import { Icon } from '@repo/ui';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

/**
 * Utility: Determine if file is a PDF based on MIME type or URL
 */
const isPdf = (mimeType?: string, url?: string): boolean => {
  if (mimeType) return mimeType.includes('pdf');
  return url?.toLowerCase().endsWith('.pdf') || false;
};

interface UniversalViewerProps {
  signedUrl: string;
  mimeType?: string;
  fileName?: string;
  className?: string;
  onClose?: () => void;
}

/**
 * UniversalViewer Component
 *
 * Renders documents using the appropriate viewer:
 * - PDFs: React PDF Viewer (@react-pdf-viewer/core)
 * - Other docs: React Doc Viewer Plus (react-doc-viewer-plus)
 *
 * Works with signed URLs (no auth headers required)
 * Styled with Tailwind CSS
 */
export function UniversalViewer({
  signedUrl,
  mimeType,
  fileName,
  className = '',
  onClose,
}: UniversalViewerProps) {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const pdfLayoutPlugin = defaultLayoutPlugin();
  const pdf = isPdf(mimeType, signedUrl);

  const handleError = (err: Error) => {
    console.error('Document viewer error:', err);
    setError(err.message || 'Failed to load document');
    setLoading(false);
  };

  const handleLoadSuccess = () => {
    setLoading(false);
  };

  return (
    <div
      className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg flex flex-col h-full ${className}`}
    >
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-t-2xl">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <Icon
            name={pdf ? 'HiDocumentText' : 'HiDocument'}
            className="h-5 w-5 text-xtnd-blue flex-shrink-0"
          />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
            {fileName || 'Document Preview'}
          </h2>
        </div>

        <div className="flex items-center space-x-2">
          {/* Open in new tab */}
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-xtnd-blue hover:text-xtnd-blue-600 hover:bg-xtnd-light rounded-md transition-colors"
            title="Open in new tab"
          >
            <Icon name="HiExternalLink" className="h-4 w-4" />
            <span>Open</span>
          </a>

          {/* Close button */}
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-md transition-colors"
              title="Close viewer"
            >
              <Icon name="HiX" className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && !error && (
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-xtnd-blue mb-4"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Loading document...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-6">
          <div className="max-w-md text-center">
            <Icon name="HiExclamationCircle" className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Failed to Load Document
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{error}</p>
            <div className="space-x-3">
              <button
                onClick={() => {
                  setError(null);
                  setLoading(true);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-xtnd-blue hover:bg-xtnd-blue-600 rounded-md transition-colors"
              >
                Try Again
              </button>
              <a
                href={signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-4 py-2 text-sm font-medium text-xtnd-blue hover:text-xtnd-blue-600 border border-xtnd-blue hover:border-xtnd-blue-600 rounded-md transition-colors"
              >
                Open in New Tab
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer */}
      {!error && (
        <div className={`flex-1 overflow-hidden ${loading ? 'hidden' : ''}`}>
          {pdf ? (
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
              <PDFViewer
                fileUrl={signedUrl}
                plugins={[pdfLayoutPlugin]}
                onDocumentLoad={handleLoadSuccess}
              />
            </Worker>
          ) : (
            <DocViewer
              documents={[{ uri: signedUrl }]}
              pluginRenderers={DocViewerRenderers}
              style={{ width: '100%', height: '100%' }}
              config={{
                header: {
                  disableHeader: true,
                  disableFileName: true,
                },
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
